import { useEffect, useRef, useState } from "react";

/**
 * A click at the song's own tempo.
 *
 * Scheduled ahead of time rather than on a timer: setInterval drifts badly
 * enough to hear within a minute, so each click is booked with the audio clock
 * a fraction of a second before it sounds, and a slow timer only tops up the
 * queue.
 */
export function Metronome({ bpm, beatsPerBar }: { bpm: number; beatsPerBar: number }) {
  const [running, setRunning] = useState(false);
  const [tempo, setTempo] = useState(Math.round(bpm) || 100);
  const context = useRef<AudioContext | null>(null);
  const nextBeat = useRef(0);
  const beat = useRef(0);

  useEffect(() => setTempo(Math.round(bpm) || 100), [bpm]);

  useEffect(() => {
    if (!running) return;

    const audio = context.current ?? new AudioContext();
    context.current = audio;
    void audio.resume();
    nextBeat.current = audio.currentTime + 0.1;
    beat.current = 0;

    const click = (at: number, accented: boolean) => {
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      oscillator.frequency.value = accented ? 1600 : 1050;
      gain.gain.setValueAtTime(accented ? 0.5 : 0.28, at);
      gain.gain.exponentialRampToValueAtTime(0.0008, at + 0.05);
      oscillator.connect(gain).connect(audio.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.06);
    };

    const pump = () => {
      const seconds = 60 / tempo;
      // Book everything due in the next quarter second.
      while (nextBeat.current < audio.currentTime + 0.25) {
        click(nextBeat.current, beat.current % Math.max(1, beatsPerBar) === 0);
        nextBeat.current += seconds;
        beat.current += 1;
      }
    };

    pump();
    const timer = window.setInterval(pump, 90);
    return () => {
      window.clearInterval(timer);
    };
  }, [running, tempo, beatsPerBar]);

  useEffect(() => () => { void context.current?.close(); }, []);

  return (
    <span className="stepper">
      <button
        className="btn"
        aria-pressed={running}
        onClick={() => setRunning((on) => !on)}
        title={running ? "Stop the click" : `Click at ${tempo} bpm`}
      >{running ? "Stop click" : "Click"}</button>
      {running && (
        <>
          <button className="btn stepper-btn" aria-label="Slower"
                  onClick={() => setTempo((t) => Math.max(30, t - 2))}>−</button>
          <span className="mono stepper-value">{tempo}</span>
          <button className="btn stepper-btn" aria-label="Faster"
                  onClick={() => setTempo((t) => Math.min(240, t + 2))}>+</button>
        </>
      )}
    </span>
  );
}
